/* Afina Mystic — product-form.js
 * Custom element wrapping the product form. Progressive enhancement: posts to
 * /cart/add.js via fetch, updates cart count in the header. Falls back to a
 * full-page POST if JS is unavailable or the endpoint errors.
 */
(function () {
  'use strict';
  if (customElements.get('product-form')) return;

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.initVariantPicker();
    }

    initVariantPicker() {
      const dataEl = this.querySelector('[data-variants-json]');
      if (!dataEl) return;
      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (_) {
        return;
      }
      if (!Array.isArray(this.variants) || !this.variants.length) return;

      this.optionGroups = Array.from(this.querySelectorAll('.product__info-options-toggle'));
      if (!this.optionGroups.length) return;

      this.variantIdInput = this.form.querySelector('[data-variant-id]');
      this.variantSelect = this.form.querySelector('[data-variant-select]');
      this.priceWrapper = this.form.querySelector('[data-price-wrapper]');
      this.gallery = document.querySelector('.product-gallery');
      this.addLabel = this.form.querySelector('[data-add-label]');

      this.optionGroups.forEach((group) => {
        group.addEventListener('click', (e) => {
          const btn = e.target.closest('.product__info-option');
          if (!btn || !group.contains(btn)) return;
          this.selectOption(group, btn);
          this.applyVariant();
        });
      });
    }

    selectOption(group, btn) {
      group.querySelectorAll('.product__info-option').forEach((b) => {
        const active = b === btn;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-checked', active ? 'true' : 'false');
      });
    }

    getSelectedOptions() {
      return this.optionGroups.map((group) => {
        const active = group.querySelector('.product__info-option.is-active');
        return active ? active.dataset.optionValue : null;
      });
    }

    findVariant(values) {
      return this.variants.find((v) =>
        v.options.length === values.length &&
        v.options.every((opt, i) => opt === values[i])
      );
    }

    applyVariant() {
      const values = this.getSelectedOptions();
      if (values.some((v) => v == null)) return;
      const variant = this.findVariant(values);
      if (!variant) return;

      if (this.variantIdInput) this.variantIdInput.value = variant.id;
      if (this.variantSelect) this.variantSelect.value = String(variant.id);
      this.updatePrice(variant);
      this.updateGallery(variant);
      this.updateAvailability(variant);

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    updatePrice(variant) {
      if (!this.priceWrapper) return;
      let html = `<span class="product__info-about-price-new" data-price>${variant.price}</span>`;
      if (variant.compare_at_price) {
        const tpl = this.priceWrapper.dataset.saveTemplate || 'Save __P__%';
        const saveText = tpl.replace('__P__', variant.discount_percent);
        html += `<span class="product__info-about-price-old" data-compare-price>${variant.compare_at_price}</span>`;
        html += `<span class="product__info-about-price-discount" data-save>${saveText}</span>`;
      }
      this.priceWrapper.innerHTML = html;
    }

    updateGallery(variant) {
      if (!this.gallery || !variant.featured_media_id) return;
      const thumb = this.gallery.querySelector(`.product-gallery__thumb[data-id="${variant.featured_media_id}"]`);
      if (thumb && !thumb.classList.contains('is-active')) thumb.click();
    }

    updateAvailability(variant) {
      const submitBtn = this.form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.disabled = !variant.available;
      if (this.addLabel && !variant.available) {
        this.addLabel.textContent = this.addLabel.dataset.soldOutLabel || this.addLabel.textContent;
      }
    }

    async onSubmit(e) {
      e.preventDefault();
      const submitBtn = this.form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.classList.add('is-loading');

      try {
        const body = new FormData(this.form);
        body.append('sections_url', window.location.pathname);

        // Подписка/разовая (Appstle): гарантированно передаём выбор плана
        if (document.querySelector('input[name="selling_plan"]')) {
          const plan = document.querySelector('input[name="selling_plan"]:checked');
          const planValue = plan ? (plan.value || '').trim() : '';
          if (planValue) {
            // подписка — Shopify сам покажет имя плана, своё свойство не дублируем
            body.set('selling_plan', planValue);
            body.delete('properties[Purchase type]');
          } else {
            body.delete('selling_plan');
            body.set('properties[Purchase type]', 'One-time purchase');
          }
        }
        const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body
        });
        if (!res.ok) throw new Error('Add to cart failed');
        await res.json();

        const cartRes = await fetch(`${window.Shopify.routes.root}cart.js`);
        const cart = await cartRes.json();
        const countEl = document.querySelector('[data-cart-count]');
        if (countEl) countEl.textContent = cart.item_count;

        this.dispatchEvent(new CustomEvent('cart:added', { bubbles: true, detail: cart }));
        // Redirect to cart page after add (safe fallback; many themes open a drawer instead).
        window.location.href = window.Shopify.routes.root + 'cart';
      } catch (err) {
        // Fallback: native form submission
        this.form.submit();
      } finally {
        if (submitBtn) submitBtn.classList.remove('is-loading');
      }
    }
  }

  customElements.define('product-form', ProductForm);
})();
